/**
 * Live countdown to a fixed sale deadline for the DD promo banner.
 *
 * The deadline is authored server-side: Liquid renders it with `| date: '%s'` to a
 * Unix timestamp computed in the *shop's* admin timezone, so every visitor counts
 * down to the same instant regardless of their own browser's timezone. The
 * storefront `shop` object exposes no timezone, and a bare date string would parse
 * as either UTC or browser-local — both end the sale at the wrong moment. An
 * absolute epoch sidesteps all of that.
 *
 * The element ships with its static deadline text as fallback content; if this
 * module never loads, that text stays visible. Progressive enhancement.
 *
 * Accessibility: the digits retick every second, which would flood a screen reader,
 * so the visual display is aria-hidden (role="timer" is implicitly aria-live="off")
 * and a single polite live region announces the remaining time only when the minute
 * rolls over. A <time> element carries the machine-readable deadline. Fixed-width,
 * tabular-figure digit cells keep neighbouring content from shifting as the numbers
 * change. Under prefers-reduced-motion the seconds are dropped and the display
 * refreshes once a minute rather than every second (WCAG 2.2.2, moving content).
 */

const SECOND_MS = 1000;
const MINUTE = 60;
const HOUR = 3600;
const DAY = 86400;

const REDUCED_MOTION = '(prefers-reduced-motion: reduce)';

class DdCountdown extends HTMLElement {
  /** @type {ReturnType<typeof setInterval> | null} */
  #timer = null;
  /** Deadline as epoch milliseconds. */
  #deadline = NaN;
  /** Whether the visitor asked for reduced motion (drives cadence + seconds). */
  #reduce = false;
  /**
   * Last whole-minute value announced to the live region, to throttle updates.
   * @type {number | null}
   */
  #announcedAt = null;
  /** @type {Record<string, HTMLElement>} */
  #cells = {};
  /** @type {HTMLElement | null} */
  #liveRegion = null;

  connectedCallback() {
    // Liquid renders epoch seconds; guard against a blank or unparseable setting so
    // a misconfigured deadline leaves the server-rendered fallback text untouched.
    const seconds = Number(this.dataset.deadline);
    if (!Number.isFinite(seconds) || seconds <= 0) return;

    this.#deadline = seconds * SECOND_MS;
    this.#reduce = window.matchMedia?.(REDUCED_MOTION).matches ?? false;

    this.#build();
    this.#tick();
    // Under reduced motion the seconds cell is hidden, so a per-second tick would
    // change nothing visible — refresh once a minute instead.
    this.#timer = setInterval(() => this.#tick(), this.#reduce ? MINUTE * SECOND_MS : SECOND_MS);
  }

  disconnectedCallback() {
    if (this.#timer != null) clearInterval(this.#timer);
    this.#timer = null;
  }

  /**
   * The units to render, largest first. Seconds are meaningless at a one-minute
   * cadence, so they are dropped under reduced motion.
   * @returns {Array<[string, string]>} [key, visible label] pairs.
   */
  #units() {
    /** @type {Array<[string, string]>} */
    const units = [
      ['days', this.dataset.unitDays || 'Days'],
      ['hours', this.dataset.unitHours || 'Hours'],
      ['minutes', this.dataset.unitMinutes || 'Mins'],
    ];
    if (!this.#reduce) units.push(['seconds', this.dataset.unitSeconds || 'Secs']);
    return units;
  }

  #build() {
    this.replaceChildren();
    this.setAttribute('role', 'timer');
    this.setAttribute('aria-label', this.dataset.ariaLabel || 'Time remaining in sale');

    // A visible lead-in so the digits read unambiguously as time until the sale ends.
    const lead = document.createElement('span');
    lead.className = 'dd-countdown__lead';
    lead.textContent = this.dataset.leadLabel || 'Sale ends in';
    lead.setAttribute('aria-hidden', 'true');

    const display = document.createElement('time');
    display.className = 'dd-countdown__display';
    display.dateTime = new Date(this.#deadline).toISOString();
    // Announced via the polite region below, not per-tick.
    display.setAttribute('aria-hidden', 'true');

    this.#cells = {};
    for (const [key, label] of this.#units()) {
      const unit = document.createElement('span');
      unit.className = 'dd-countdown__unit';

      const value = document.createElement('span');
      value.className = 'dd-countdown__value';
      value.textContent = '00';

      const name = document.createElement('span');
      name.className = 'dd-countdown__label';
      name.textContent = label;

      unit.append(value, name);
      display.append(unit);
      this.#cells[key] = value;
    }

    this.#liveRegion = document.createElement('span');
    this.#liveRegion.className = 'visually-hidden';
    this.#liveRegion.setAttribute('aria-live', 'polite');

    this.append(lead, display, this.#liveRegion);
  }

  #tick() {
    const remaining = this.#deadline - Date.now();
    if (remaining <= 0) {
      this.#finish();
      return;
    }

    const total = Math.floor(remaining / SECOND_MS);
    /** @type {Record<string, number>} */
    const parts = {
      days: Math.floor(total / DAY),
      hours: Math.floor((total % DAY) / HOUR),
      minutes: Math.floor((total % HOUR) / MINUTE),
      seconds: total % MINUTE,
    };

    for (const [key, cell] of Object.entries(this.#cells)) {
      cell.textContent = String(parts[key]).padStart(2, '0');
    }

    // Announce at most once a minute rather than on every tick.
    const minuteStamp = Math.floor(total / MINUTE);
    if (this.#liveRegion && minuteStamp !== this.#announcedAt) {
      this.#announcedAt = minuteStamp;
      this.#liveRegion.textContent = this.#sentence(parts);
    }
  }

  /** @param {Record<string, number>} parts */
  #sentence(parts) {
    const template = this.dataset.announce || 'Sale ends in {days} days, {hours} hours, {minutes} minutes';
    return template
      .replace('{days}', String(parts.days))
      .replace('{hours}', String(parts.hours))
      .replace('{minutes}', String(parts.minutes));
  }

  #finish() {
    if (this.#timer != null) clearInterval(this.#timer);
    this.#timer = null;
    this.dataset.ended = 'true';

    const message = document.createElement('p');
    message.className = 'dd-countdown__ended';
    message.setAttribute('role', 'status');
    message.textContent = this.dataset.endedLabel || 'Sale ended';
    this.replaceChildren(message);
  }
}

if (!customElements.get('dd-countdown')) {
  customElements.define('dd-countdown', DdCountdown);
}
